import { pdfjs } from "react-pdf";

type PdfBinaryLike =
    | Uint8Array
    | ArrayBuffer
    | number[]
    | { [k: string]: number } // array-like object coming from JSON (keys: "0","1",...)
    | null
    | undefined;

export interface PdfLoaderInput {
    filePath?: string; // remote or local public path
    fileContent?: PdfBinaryLike; // various representations of the PDF binary
}

export const PdfLoader = async ({ filePath, fileContent }: PdfLoaderInput) => {
    // Configure worker (must be set before calling getDocument)
    pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

    let data: Uint8Array;
    if (fileContent) {
        data = toUint8Array(fileContent);
    } else if (filePath) {
        const res = await fetch(filePath);
        if (!res.ok) throw new Error(`Failed to fetch PDF at ${filePath}: ${res.status}`);
        data = new Uint8Array(await res.arrayBuffer());
    } else {
        throw new Error("Either filePath or fileContent must be provided to load PDF.");
    }

    // Obtain metadata (numPages) without rendering any page yet
    const task = pdfjs.getDocument({ data });
    try {
        const pdf = await task.promise;
        return { file: data, totalPages: pdf.numPages };
    } finally {
        task.destroy();
    }
};

function toUint8Array(input: PdfBinaryLike): Uint8Array {
    if (!input) throw new Error("No PDF binary provided");
    if (input instanceof Uint8Array) return input;
    if (input instanceof ArrayBuffer) return new Uint8Array(input);
    if (Array.isArray(input)) return new Uint8Array(input);
    if (typeof input === "object") {
        // JSON-serialized Uint8Array or array-like with numeric (integer) keys.
        const values = Object.values(input);
        if (values.length === 0) {
            throw new Error("PDF binary object does not contain numeric indices");
        }
        return new Uint8Array(values);
    }
    throw new Error("Unsupported PDF binary format");
}
